import { createContext, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
// utils
import axios from "../utils/axios";
import { isValidToken, setSession } from "../utils/jwt";
import useLocales from "../hooks/useLocales";
// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  UPDATE: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: "jwt",
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  update: () => Promise.resolve(),
  refresh: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { onChangeLang } = useLocales();

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          const response = await axios.get("/api/v1/auth/user");
          const user = response.data;

          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.log(err?.response.statusText);
        if (err?.response?.status === 401) {
          logout();
        }
      }
    };

    initialize();
  }, []);

  const login = async (phone, password) => {
    const response = await axios.post("/api/v1/auth/login", {
      phone,
      password,
    });
    const { accessToken, user } = response.data;

    if (user?.role !== "vendor") {
      return false;
    }

    setSession(accessToken);
    dispatch({
      type: "LOGIN",
      payload: {
        user,
      },
    });
    return true;
  };

  const logout = async () => {
    try {
      await axios.post("/api/v1/auth/logout", {});
      setSession(null);
      dispatch({ type: "LOGOUT" });
    } catch (e) {
      setSession(null);
      dispatch({ type: "LOGOUT" });
    }
  };

  const update = async (data) => {
    const response = await axios.put("/api/v1/accounts/update", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    dispatch({
      type: "UPDATE",
      payload: {
        user: response.data,
      },
    });
  };

  const refresh = async () => {
    const response = await axios.get("/api/v1/auth/user");
    const user = response.data;

    dispatch({
      type: "INITIALIZE",
      payload: {
        isAuthenticated: true,
        user,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        login,
        logout,
        update,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
